import { useEffect, useState, type ReactNode } from "react";

import { Alert, Badge, Button, Label, Tab, TabList, Textarea } from "@realroof/ui";

import { api, canSeeAudit } from "../api";
import { useSession } from "../session";
import { when } from "../format";
import { Table, td, th } from "../table";

interface OrderAdmin {
  id: string;
  number: string;
  pack_name: string;
  ads: number;
  total: number;
  status: "created" | "paid" | "failed" | "refunded" | "part_refunded";
  refund: number;
  refund_reason: string | null;
  refunded_at: string | null;
  coupon_code: string | null;
  invoice_number: string | null;
  billing_name: string;
  gstin: string | null;
  state: string;
  provider_payment_id: string | null;
  created_at: string;
  paid_at: string | null;
  user_mobile: string | null;
  user_email: string | null;
}

type Filter = "" | "paid" | "failed" | "created" | "refunded" | "part_refunded";
const TABS: [Filter, string][] = [
  ["", "All"],
  ["paid", "Paid"],
  ["refunded", "Refunded"],
  ["part_refunded", "Partly refunded"],
  ["failed", "Failed"],
  ["created", "Not completed"],
];
const TONE = { paid: "success", failed: "danger", created: "neutral", refunded: "warning", part_refunded: "warning" } as const;
const LABEL = { paid: "Paid", failed: "Failed", created: "Not completed", refunded: "Refunded", part_refunded: "Partly refunded" } as const;

/** Every checkout, newest first, with the Razorpay payment reference for support questions. */
export function Orders() {
  const { me } = useSession();
  const canRefund = canSeeAudit(me?.staff_role); // admins and super admins
  const [message, setMessage] = useState<{ tone: "error" | "success"; text: string } | null>(null);
  const [filter, setFilter] = useState<Filter>("");
  const [rows, setRows] = useState<OrderAdmin[] | null>(null);
  const [error, setError] = useState("");
  const [refunding, setRefunding] = useState<OrderAdmin | null>(null); // the refund dialog's order
  const [popup, setPopup] = useState<{ tone: "error" | "success"; title: string; text: string } | null>(null);

  const load = () =>
    api<OrderAdmin[]>(`/admin/orders${filter ? `?status=${filter}` : ""}`)
      .then((list) => (setRows(list), setError("")))
      .catch((err: Error) => setError(err.message));
  useEffect(() => {
    setRows(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  /** The status changes only when the API says Razorpay took the refund; then a popup says so. */
  async function refund(o: OrderAdmin, reason: string) {
    setMessage(null);
    try {
      const done = await api<OrderAdmin>(`/admin/orders/${o.id}/refund`, { method: "POST", body: { reason } });
      setRefunding(null);
      setPopup({
        tone: "success",
        title: "Refund done",
        text: `${o.number}: ₹${done.refund.toFixed(2)} refunded through Razorpay. It reaches the buyer's bank in 5-7 working days; the unused credits are removed.`,
      });
      await load();
    } catch (err) {
      setRefunding(null);
      setPopup({ tone: "error", title: "Refund failed", text: `${o.number} was not refunded and is still paid. ${(err as Error).message}` });
    }
  }

  const paid = rows?.filter((r) => r.status === "paid").reduce((sum, r) => sum + r.total, 0) ?? 0;

  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Orders</h1>
      <TabList>
        {TABS.map(([f, label]) => (
          <Tab key={f || "all"} selected={f === filter} className="flex-none" onClick={() => setFilter(f)}>
            {label}
          </Tab>
        ))}
      </TabList>
      {error && <Alert tone="error">{error}</Alert>}
      {message && <Alert tone={message.tone}>{message.text}</Alert>}
      {rows && rows.length > 0 && (
        <p className="text-sm text-muted-foreground">
          {rows.length} orders shown · paid ₹{paid.toLocaleString("en-IN", { minimumFractionDigits: 2 })} incl. GST
        </p>
      )}
      {rows?.length === 0 && <p className="text-muted-foreground">No orders.</p>}
      {rows && rows.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th className={th}>Order</th>
              <th className={th}>Buyer</th>
              <th className={th}>Pack</th>
              <th className={th}>Total</th>
              <th className={th}>Status</th>
              <th className={th}>When</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.id}>
                <td className={td}>
                  <span className="font-mono text-sm">{o.number}</span>
                  {o.provider_payment_id && <div className="font-mono text-xs text-muted-foreground">{o.provider_payment_id}</div>}
                </td>
                <td className={td}>
                  {o.billing_name}
                  <div className="text-xs text-muted-foreground">
                    {o.user_mobile} · {o.state}
                    {o.gstin ? ` · GSTIN ${o.gstin}` : ""}
                  </div>
                </td>
                <td className={td}>{o.pack_name}</td>
                <td className={`${td} whitespace-nowrap`}>
                  ₹{o.total.toFixed(2)}
                  {o.coupon_code && <div className="text-xs text-success">coupon {o.coupon_code}</div>}
                  {o.refund > 0 && <div className="text-xs text-destructive">refunded ₹{o.refund.toFixed(2)}</div>}
                  {o.refunded_at && <div className="text-xs text-muted-foreground">on {when(o.refunded_at)}</div>}
                  {o.refund_reason && <div className="max-w-56 text-xs text-muted-foreground">“{o.refund_reason}”</div>}
                </td>
                <td className={td}>
                  <Badge tone={TONE[o.status]}>{LABEL[o.status]}</Badge>
                  {o.invoice_number && <div className="font-mono text-xs text-muted-foreground">{o.invoice_number}</div>}
                </td>
                <td className={`${td} whitespace-nowrap`}>{when(o.paid_at ?? o.created_at)}</td>
                <td className={td}>
                  {canRefund && o.status === "paid" && o.total > 0 && (
                    <Button size="sm" variant="destructive" onClick={() => setRefunding(o)}>
                      Refund…
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {refunding && <RefundDialog order={refunding} onRefund={(reason) => refund(refunding, reason)} onClose={() => setRefunding(null)} />}
      {popup && (
        <Modal title={popup.title} onClose={() => setPopup(null)}>
          <Alert tone={popup.tone}>{popup.text}</Alert>
          <div className="flex justify-end">
            <Button variant="primary" onClick={() => setPopup(null)}>
              OK
            </Button>
          </div>
        </Modal>
      )}
    </section>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
      <div role="dialog" aria-modal="true" aria-label={title} className="flex w-full max-w-md flex-col gap-3 rounded-xl border border-border bg-card p-5 shadow-xl">
        <h2 className="text-lg font-bold">{title}</h2>
        {children}
      </div>
    </div>
  );
}

/** Refund an order: the reason (the buyer sees it) and a clear warning of what happens. */
function RefundDialog({ order, onRefund, onClose }: { order: OrderAdmin; onRefund: (reason: string) => Promise<void>; onClose: () => void }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const short = reason.trim().length < 5;
  return (
    <Modal title={`Refund ${order.number}`} onClose={() => !busy && onClose()}>
      <p className="m-0 text-sm">
        ₹{order.total.toFixed(2)} goes back to {order.billing_name} through Razorpay. The order&apos;s unused credits are removed, and ads made premium with
        it become free ads.
      </p>
      <form
        className="flex flex-col gap-3"
        onSubmit={async (e) => {
          e.preventDefault();
          if (short) return;
          setBusy(true);
          await onRefund(reason.trim());
          setBusy(false);
        }}
      >
        <Label>
          Reason (the buyer sees it)
          <Textarea rows={3} maxLength={200} value={reason} autoFocus onChange={(e) => setReason(e.target.value)} />
        </Label>
        {reason && short && <p className="m-0 text-sm text-destructive">Write at least 5 characters.</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="destructive" disabled={busy || short}>
            {busy ? "Refunding…" : "Refund now"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
